"use client";

import { Box, Flex, Icon } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { HiOutlineChevronRight } from "react-icons/hi2";
import type { GetTeacherResponse } from "@/openapi/generated";
import { EntryType, MediaGroup } from "@/openapi/generated/models";
import {
  usePresignedImageErrorRecovery,
  usePublicListingMediaUrls,
} from "@/features/media/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useTenantData } from "@/hooks";
import { getTeacherMediaQueryPrefix } from "@/lib/utils/media/teacher-profile-image-query";
import { buildTeacherPublicPath, appendFromListingQuery } from "@/lib/utils/publicPaths";
import {
  getTeacherPublicDisplayName,
  getTeacherPublicShortName,
} from "@/lib/utils/teacherPublic";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Link, Text } from "@/components/ui";
import { ImageWithFallback } from "@/components/shared";

export interface TeachersListProps {
  data: GetTeacherResponse[];
}

interface TeacherListItemProps {
  teacher: GetTeacherResponse;
  href: string;
  imageUrl: string | null;
  isLast: boolean;
}

const TeacherListItem = ({
  teacher,
  href,
  imageUrl,
  isLast,
}: TeacherListItemProps) => {
  const theme = useThemeTokens();
  const queryClient = useQueryClient();
  const { tenantId } = useTenantData();
  const teacherId = Number(teacher.teacherId);

  const refreshImage = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: getTeacherMediaQueryPrefix(tenantId, teacherId),
        exact: false,
      }),
    [queryClient, tenantId, teacherId],
  );

  const handleProfileImageError = usePresignedImageErrorRecovery(
    imageUrl,
    refreshImage,
  );

  const { heading, subline } = getTeacherPublicDisplayName(teacher);
  const shortName = getTeacherPublicShortName(teacher);
  const bioPreview = teacher.biography?.trim();
  const locationNames = (teacher.workLocations ?? [])
    .map((loc) => loc.name?.trim())
    .filter(Boolean);

  return (
    <Link
      href={href}
      display="block"
      py={{ base: 5, md: 6 }}
      borderBottomWidth={isLast ? 0 : "1px"}
      borderColor={theme.borderGlass.subtle}
      outline="none"
      transition="border-color 0.2s ease"
      _hover={{ textDecoration: "none" }}
      _focusVisible={{
        boxShadow: theme.focusRing,
        borderRadius: "8px",
      }}
      aria-label={`View profile for ${shortName}`}
      sx={{
        "@media (hover: hover)": {
          "&:hover [data-teacher-name]": {
            color: theme.accent.primary,
          },
        },
      }}
    >
      <Flex
        direction={{ base: "column", lg: "row" }}
        align={{ base: "stretch", lg: "flex-start" }}
        gap={{ base: 4, lg: 8 }}
      >
        <Box
          flexShrink={0}
          w={{ base: "72px", lg: "88px" }}
          borderRadius="10px"
          overflow="hidden"
          aspectRatio="3 / 4"
          borderWidth="1px"
          borderColor={theme.borderGlass.default}
        >
          <ImageWithFallback
            src={imageUrl}
            alt={shortName}
            fallbackType="avatar"
            width="100%"
            height="100%"
            borderRadius="0"
            objectFit="cover"
            onImageError={handleProfileImageError}
          />
        </Box>

        <Flex direction="column" flex={1} minW={0} gap={2}>
          <Box>
            <Text
              as="h2"
              data-teacher-name
              variant="primary"
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="600"
              letterSpacing="-0.02em"
              lineHeight="1.25"
              transition="color 0.2s ease"
              noOfLines={2}
            >
              {heading}
            </Text>
            {subline ? (
              <Text variant="secondary" fontSize="sm" mt={1} noOfLines={1}>
                {subline}
              </Text>
            ) : null}
          </Box>

          {bioPreview ? (
            <Text
              variant="secondary"
              fontSize="sm"
              lineHeight="1.6"
              noOfLines={{ base: 3, md: 2 }}
            >
              {bioPreview}
            </Text>
          ) : null}

          {locationNames.length > 0 ? (
            <Text variant="tertiary" fontSize="sm" lineHeight="1.5">
              {locationNames.join(" · ")}
            </Text>
          ) : null}
        </Flex>

        <Flex
          flexShrink={0}
          align={{ base: "flex-end", lg: "center" }}
          justify="flex-end"
          minW={{ lg: "100px" }}
          pt={{ lg: 2 }}
          color={theme.text.tertiary}
          fontSize="sm"
          fontWeight="500"
          gap={1}
        >
          <Text as="span">Profile</Text>
          <Icon as={HiOutlineChevronRight} boxSize="16px" aria-hidden />
        </Flex>
      </Flex>
    </Link>
  );
};

export const TeachersList = ({ data }: TeachersListProps) => {
  const theme = useThemeTokens();
  const ownerIds = useMemo(
    () => data.map((teacher) => Number(teacher.teacherId)).filter((id) => id > 0),
    [data],
  );
  const imageUrls = usePublicListingMediaUrls({
    ownerType: EntryType.Teacher,
    ownerIds,
    mediaGroup: MediaGroup.Profile,
  });

  return (
    <Box
      as="ul"
      listStyleType="none"
      m={0}
      p={0}
      borderTopWidth="1px"
      borderColor={theme.borderGlass.subtle}
    >
      {data.map((teacher, index) => {
        const teacherId = Number(teacher.teacherId);
        const href = appendFromListingQuery(
          buildTeacherPublicPath({
            firstName: teacher.firstName,
            lastName: teacher.lastName,
            teacherId,
          }),
          "teachers",
        );

        return (
          <Box as="li" key={String(teacher.teacherId)} listStyleType="none">
            <TeacherListItem
              teacher={teacher}
              href={href}
              imageUrl={imageUrls[teacherId] ?? null}
              isLast={index === data.length - 1}
            />
          </Box>
        );
      })}
    </Box>
  );
};
